import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CalendarCheck, FileText } from 'lucide-react';
import { useSeo } from '@/lib/seo';
import { GROWTH_SERVICES } from '@/data/growth';
import { LAUNCH_SERVICES } from '@/data/launch';
import { PEOPLE_SERVICES } from '@/data/people';
import { trackEvent } from '@/lib/analytics';
import { Reveal } from '@/components/site/Reveal';
import { CtaBand } from '@/components/site/CtaBand';

// Generic service detail page for the division catalogues (Amden Growth,
// Launch, People). Renders from the approved service data and routes every
// CTA into the enquiry/booking flow with the service preselected — no dead
// ends, no invented content.
const DIVISIONS = {
  growth: {
    name: 'Amden Growth', route: '/services/growth', services: GROWTH_SERVICES,
    accent: '#029E88', area: 'growth',
  },
  launch: {
    name: 'Amden Launch', route: '/services/launch', services: LAUNCH_SERVICES,
    accent: '#4225C4', area: 'launch',
  },
  people: {
    name: 'Amden People', route: '/services/people', services: PEOPLE_SERVICES,
    accent: '#D6318B', area: 'people',
  },
};

const tracked = (name, division, service) =>
  `/contact?area=${division.area}&service=${encodeURIComponent(service)}&source=${encodeURIComponent(`${division.name} — ${service}`)}`;

export default function ServiceDetailPage({ division }) {
  const { slug } = useParams();
  const div = DIVISIONS[division];
  const service = div?.services.find((s) => s.slug === slug) || null;

  useSeo({
    title: service ? `${service.name} | ${div.name} | Amden Group` : 'Service | Amden Group',
    description: service?.shortDescription || 'Practical business support from Amden Group.',
    canonical: `https://amden.ie/${division}/${slug}`,
    robots: service ? undefined : 'noindex',
    jsonLd: service ? {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://amden.ie/' },
            { '@type': 'ListItem', position: 2, name: div.name, item: `https://amden.ie${div.route}` },
            { '@type': 'ListItem', position: 3, name: service.name, item: `https://amden.ie/${division}/${slug}` },
          ],
        },
        {
          '@type': 'Service',
          name: service.name,
          description: service.shortDescription,
          serviceType: service.category,
          provider: { '@type': 'Organization', name: 'Amden Group', url: 'https://amden.ie' },
        },
      ],
    } : undefined,
  });

  if (!service) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 bg-white px-4 text-center" data-testid="service-notfound">
        <h1 className="text-2xl font-extrabold tracking-tight text-navy-900">Service not found</h1>
        <p className="max-w-md text-sm leading-relaxed text-slate-600">
          This service may have been moved. Browse the full range on our services page.
        </p>
        <Link to="/services" className="mt-2 inline-flex items-center gap-2 rounded-md bg-navy-900 px-5 py-2.5 text-sm font-bold text-white hover:bg-navy-700">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" /> All services
        </Link>
      </div>
    );
  }

  const consult = service.purchaseMode === 'book_consultation';
  const related = div.services.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <>
      <section aria-labelledby="service-title" data-testid="service-detail" className="relative overflow-hidden bg-navy-900 py-16 sm:py-24">
        <div className="grain-overlay" aria-hidden="true" />
        <div className="relative mx-auto max-w-4xl px-4 sm:px-6">
          <nav aria-label="Breadcrumb" className="text-xs font-semibold text-slate-400">
            <Link to={div.route} className="inline-flex items-center gap-1.5 transition-colors hover:text-gold-500" data-testid="service-back">
              <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" /> {div.name}
            </Link>
          </nav>
          <Reveal>
            <p className="mt-8 text-xs font-bold uppercase tracking-[0.22em]" style={{ color: div.accent }}>
              {service.category}
            </p>
            <h1 id="service-title" className="mt-3 text-balance text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl" data-testid="service-title">
              {service.name}
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
              {service.shortDescription}
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              {consult ? (
                <Link
                  to={`${tracked('consult', div, service.name)}#book-appointment`}
                  data-testid="service-cta"
                  onClick={() => trackEvent('cta_book_consultation')}
                  className="group inline-flex items-center gap-2 rounded-md bg-gold-500 px-6 py-3.5 text-sm font-bold text-navy-950 transition-colors hover:bg-gold-600"
                >
                  <CalendarCheck className="h-4 w-4" aria-hidden="true" />
                  Book a Consultation
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
                </Link>
              ) : (
                <Link
                  to={`${tracked('quote', div, service.name)}&topic=pricing#contact-enquiry`}
                  data-testid="service-cta"
                  onClick={() => trackEvent('cta_contact')}
                  className="group inline-flex items-center gap-2 rounded-md bg-gold-500 px-6 py-3.5 text-sm font-bold text-navy-950 transition-colors hover:bg-gold-600"
                >
                  <FileText className="h-4 w-4" aria-hidden="true" />
                  Request a Quote
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
                </Link>
              )}
              <Link
                to={`${tracked('discuss', div, service.name)}#contact-enquiry`}
                data-testid="service-discuss"
                className="inline-flex items-center gap-2 rounded-md border-2 border-white/25 px-6 py-3 text-sm font-bold text-white transition-colors hover:border-gold-500 hover:text-gold-500"
              >
                Ask a Question
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {related.length > 0 && (
        <section aria-labelledby="service-related-heading" className="bg-mist py-16 sm:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <h2 id="service-related-heading" className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
              More from {div.name}
            </h2>
            <ul className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((item) => (
                <li key={item.slug} className="h-full">
                  <Link
                    to={`/${division}/${item.slug}`}
                    data-testid={`service-related-${item.slug}`}
                    className="group flex h-full flex-col rounded-xl border border-slate-200 bg-white p-6 transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-lift focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900"
                  >
                    <p className="text-xs font-bold uppercase tracking-widest" style={{ color: div.accent }}>{item.category}</p>
                    <p className="mt-2 text-base font-extrabold tracking-tight text-navy-900">{item.name}</p>
                    <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{item.shortDescription}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-navy-900">
                      Details <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      <CtaBand
        eyebrow={div.name}
        heading={`Talk to us about ${service.name.toLowerCase()}`}
        copy="Tell us where your business needs support — we will outline a practical approach and honest pricing."
        primaryLabel="Book a Consultation"
        primaryHref={`${tracked('consult', div, service.name)}#book-appointment`}
        secondaryLabel="Send an Enquiry"
        secondaryHref={`${tracked('discuss', div, service.name)}#contact-enquiry`}
      />
    </>
  );
}
